import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommentStore } from '@/store/commentStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import type { AnnotationData } from './AnnotationCanvas.tsx';
import styles from './Viewer.module.css';

// ── Doodle Management Dialog ─────────────────────────────────────────

export function DoodleDialog({ shotId, isCreator: isCreatorProp, onClose }: { shotId: number; isCreator: boolean; onClose: () => void }) {
  const { t } = useTranslation('viewer');
  const cs = useCommentStore;
  const myName = cs(s => s.reviewerName);
  const allComments = cs(s => s.comments);

  const doodleComments = allComments.filter(
    c => c.shotId === shotId && c.annotationSvg
  );
  const myDoodle = doodleComments.find(c => c.author === myName);
  const otherDoodles = doodleComments.filter(c => c.author !== myName);
  const otherAuthors = [...new Set(otherDoodles.map(c => c.author))];

  const handleDraw = useCallback(() => {
    onClose();
    useUiStore.getState().toggleDrawingMode();
  }, [onClose]);

  const handleClearAll = useCallback(() => {
    const state = cs.getState();
    for (const c of doodleComments) {
      state.deleteComment(c.id);
    }
    onClose();
    useUiStore.getState().toggleDrawingMode();
  }, [doodleComments, onClose]);

  const handleClearMine = useCallback(() => {
    if (myDoodle) {
      cs.getState().deleteComment(myDoodle.id);
    }
    onClose();
  }, [myDoodle, onClose]);

  // Creator with others' doodles: show author names + Clear All option
  const showCreatorView = isCreatorProp && otherDoodles.length > 0;

  // Reviewer display names come from self-writable user_metadata — any authenticated user (public
  // anon key) can set their own name to anything, including markup, and it renders here to the
  // PROJECT CREATOR. Used to interpolate `<strong>${otherAuthors.join(', ')}</strong>` into the
  // translated sentence and dangerouslySetInnerHTML the result, which would execute whatever a
  // reviewer typed as their name. Fixed the same way as ApprovalConfirmDialog.tsx /
  // MergeConfirmDialog.tsx: names never touch innerHTML. Since the placeholder sits MID-sentence
  // (not at the start, so it can't just move to its own line the way those two do it), split the
  // translated string around a sentinel standing in for {{authors}} and render the JSX <strong> in
  // between the two text halves — the emphasis survives, the names are always plain React-escaped
  // text content, and translators still control where the name falls in their language's word order.
  // Written as the escape form, not a raw codepoint, so the sentinel stays reviewable/copy-safe
  // in source and diffs — an invisible character is undetectable in review and fragile under
  // copy/paste or editor normalization. Same runtime behavior either way.
  const AUTHORS_PLACEHOLDER = '\uE000';
  const [creatorBodyPrefix, creatorBodySuffix] = showCreatorView
    ? t('doodleDialogCreatorBody', { authors: AUTHORS_PLACEHOLDER }).split(AUTHORS_PLACEHOLDER)
    : ['', ''];

  return (
    <div className={styles.aiConfirmPopover}>
      <div className={styles.aiConfirmHeader}>
        <span className={styles.aiConfirmHeaderIcon}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </span>
        <span className={styles.aiConfirmTitle}>
          {showCreatorView ? t('doodleDialogTitleCreator') : t('doodleDialogTitleReviewer')}
        </span>
      </div>

      <div className={styles.doodleDialogBody}>
        {showCreatorView ? (
          <span>
            {creatorBodyPrefix}
            <strong>{otherAuthors.join(', ')}</strong>
            {creatorBodySuffix}
            {myDoodle ? t('doodleDialogCreatorBodyAndYou') : ''}
          </span>
        ) : (
          <span>{t('doodleDialogReviewerBody')}</span>
        )}
      </div>

      <div className={styles.aiConfirmButtons}>
        <button className={styles.aiConfirmBtnCancel} onClick={onClose}>{t('doodleCancelButton')}</button>
        {myDoodle && (
          <button className={styles.doodleDialogBtnDanger} onClick={handleClearMine}>
            {t('clearMyDoodleButton')}
          </button>
        )}
        {showCreatorView && (
          <button className={styles.doodleDialogBtnDanger} onClick={handleClearAll}>
            {t('clearAllDoodlesButton')}
          </button>
        )}
        <button className={styles.aiConfirmBtnGo} onClick={handleDraw}>
          {myDoodle ? t('redrawButton') : t('drawButton')}
        </button>
      </div>
    </div>
  );
}

// ── Post-Draw Comment Prompt ─────────────────────────────────────────

export function PostDrawPrompt({ shotId }: { shotId: number }) {
  const { t } = useTranslation('viewer');
  const cs = useCommentStore;
  const myName = cs(s => s.reviewerName);
  const addComment = cs(s => s.addComment);
  const paths = useUiStore(s => s.postDrawPromptPaths);
  const screenplayPage = useUiStore(s => s.postDrawPromptScreenplayPage);
  const sceneId = useUiStore(s => s.postDrawPromptSceneId);
  const closePrompt = useUiStore(s => s.closePostDrawPrompt);

  const isPageAnnotation = screenplayPage != null && sceneId != null;
  const [text, setText] = useState(isPageAnnotation ? t('postDrawScriptDefaultText', { reviewerName: myName }) : t('postDrawDefaultText'));

  const handleSubmit = useCallback(() => {
    if (!paths || paths.length === 0) { closePrompt(); return; }

    const state = cs.getState();

    if (isPageAnnotation) {
      // Snapshot model: page annotations accumulate as history (no replace-own).
      // Only the latest is displayed; previous snapshots stay in DB for history.
      // const prevOwn = state.comments.find(
      //   c => c.screenplayPage === screenplayPage && c.sceneId === sceneId && c.annotationSvg && c.author === myName
      // );
      // if (prevOwn) state.deleteComment(prevOwn.id);

      const data: AnnotationData = { paths, viewBox: '0 0 1 1' };
      addComment(null, null, text.trim() || 'Doodled.', undefined, JSON.stringify(data), screenplayPage, sceneId);
    } else {
      // Replace-own: delete my previous doodle on this shot
      const prevOwn = state.comments.find(
        c => c.shotId === shotId && c.annotationSvg && c.author === myName && !c.screenplayPage
      );
      if (prevOwn) state.deleteComment(prevOwn.id);

      const data: AnnotationData = { paths, viewBox: '0 0 1 1' };
      addComment(shotId, null, text.trim() || 'Doodled.', undefined, JSON.stringify(data));

      // Auto-set approval to 'notes' if pending
      const approval = state.getApprovalForShot(shotId);
      if (!approval || approval.status === 'pending') state.setApproval(shotId, 'notes');
    }

    closePrompt();
  }, [paths, shotId, myName, text, addComment, closePrompt, isPageAnnotation, screenplayPage, sceneId]);

  const handleDiscard = useCallback(() => {
    if (paths && paths.length > 0) {
      if (!window.confirm(t('discardDoodleConfirm'))) return;
    }
    closePrompt();
  }, [paths, closePrompt, t]);

  return (
    <div className={styles.aiConfirmPopover}>
      <div className={styles.aiConfirmHeader}>
        <span className={styles.aiConfirmHeaderIcon}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </span>
        <span className={styles.aiConfirmTitle}>{t('addDoodleCommentTitle')}</span>
      </div>
      <textarea
        className={styles.aiConfirmTextarea}
        rows={2}
        value={text}
        onChange={e => setText(e.target.value)}
        autoFocus
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
      />
      <div className={styles.aiConfirmButtons}>
        <button className={styles.aiConfirmBtnCancel} onClick={handleDiscard}>{t('discardDoodleButton')}</button>
        <button className={styles.aiConfirmBtnGo} onClick={handleSubmit}>{t('postDoodleButton')}</button>
      </div>
    </div>
  );
}
